/**
 * Admissions (§ 8) : formulaire public de candidature (honeypot) et
 * gestion des candidatures par le comité.
 */
import React, { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { Spinner, ErrorBox, Badge, useApi, Intro, ImageProtegee } from '../components/common.jsx';
import { fmtCHF } from '../lib/format.js';
import { fmtDate } from '../lib/format.js';

/** Formulaire public d'adhésion — shortcode [adlt_candidature]. */
export function Candidature() {
  const [etat, setEtat] = useState('saisie');
  const [erreur, setErreur] = useState('');
  const [groupes, setGroupes] = useState([]);
  const [ddn, setDdn] = useState('');
  const [photo, setPhoto] = useState(null); // data URL
  const [tarifs, setTarifs] = useState(null);
  const [typeMembre, setTypeMembre] = useState('actif');
  const [foyer, setFoyer] = useState(false);

  useEffect(() => {
    api('groupes-publics').then((r) => r.ok && setGroupes(r.data));
    api('demandes/tarifs').then((r) => r.ok && setTarifs(r.data));
  }, []);

  // Cotisation : catégorie selon l'année civile de naissance, prorata des
  // mois restants (mois entamé : dès le prochain mois complet), rabais 20 %.
  const cotisation = (() => {
    if (!tarifs || !tarifs.cotisations) return null;
    const t = tarifs.cotisations;
    const annee = new Date().getFullYear();
    let categorie, annuel;
    if (typeMembre === 'soutien') { categorie = 'Membre de soutien'; annuel = t.soutien; }
    else if (!ddn) return null;
    else {
      const age = annee - parseInt(ddn.slice(0, 4), 10);
      if (age <= 15) { categorie = 'U15'; annuel = t.u15; }
      else if (age <= 20) { categorie = 'U21'; annuel = t.u21; }
      else { categorie = 'Adulte'; annuel = t.adulte; }
    }
    const mois = Math.max(0, 12 - (new Date().getMonth() + 1));
    let montant = (annuel * mois) / 12;
    if (foyer) montant *= 0.8;
    return { categorie, annuel, mois, montant: Math.round(montant) }; // arrondi au franc
  })();

  // Mineur : moins de 18 ans à ce jour.
  const mineur = (() => {
    if (!ddn) return false;
    const n = new Date(ddn);
    if (isNaN(n)) return false;
    const age = (Date.now() - n.getTime()) / (365.25 * 86400000);
    return age < 18;
  })();

  function lirePhoto(e) {
    const fichier = e.target.files && e.target.files[0];
    if (!fichier) { setPhoto(null); return; }
    if (fichier.size > 3 * 1024 * 1024) { setErreur('Photo trop lourde (3 Mo au maximum).'); e.target.value = ''; return; }
    const lecteur = new FileReader();
    lecteur.onload = () => setPhoto(lecteur.result);
    lecteur.readAsDataURL(fichier);
  }

  async function submit(e) {
    e.preventDefault();
    const f = e.target;
    setEtat('envoi'); setErreur('');
    const r = await api('candidatures', {
      method: 'POST',
      body: {
        prenom: f.prenom.value, nom: f.nom.value, date_naissance: ddn,
        genre: f.genre.value,
        adresse: f.adresse.value, npa: f.npa.value, localite: f.localite.value,
        email: f.email.value, telephone: f.telephone.value,
        groupe_id: f.groupe_id.value,
        type_membre: typeMembre, rabais_foyer: foyer ? 1 : 0,
        photo,
        parent1_nom: f.parent1_nom?.value || '', parent1_telephone: f.parent1_telephone?.value || '', parent1_email: f.parent1_email?.value || '',
        parent2_nom: f.parent2_nom?.value || '', parent2_telephone: f.parent2_telephone?.value || '', parent2_email: f.parent2_email?.value || '',
        message: f.message.value,
        consentement: f.consentement.checked ? 1 : 0,
        statuts: f.statuts.checked ? 1 : 0,
        site_web: f.site_web.value,
      },
    });
    if (r.ok) setEtat('envoye');
    else { setEtat('saisie'); setErreur(r.error); }
  }

  if (etat === 'envoye') {
    return <div className="alert alert-success">Merci ! Votre candidature a bien été transmise au responsable des admissions, qui vous répondra par courriel.</div>;
  }
  return (
    <form onSubmit={submit}>
      <h2 className="h4 mb-3">Adhésion à la Compagnie des Archers de la Tour</h2>
      {erreur && <div className="alert alert-danger py-2">{erreur}</div>}
      <input name="site_web" tabIndex="-1" autoComplete="off" style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true" />

      <div className="card adlt-etape mb-3"><div className="card-body">
        <h5 className="d-flex align-items-center gap-2 mb-3"><span className="adlt-etape-num">1</span>Identité</h5>
        <div className="row g-2">
          <div className="col-md-4"><label className="form-label">Prénom *</label><input name="prenom" className="form-control" required autoComplete="given-name" /></div>
          <div className="col-md-4"><label className="form-label">Nom *</label><input name="nom" className="form-control" required autoComplete="family-name" /></div>
          <div className="col-md-4"><label className="form-label">Genre *</label>
            <select name="genre" className="form-select" required>
              <option value="">—</option><option value="homme">Homme</option><option value="femme">Femme</option><option value="neutre">Neutre</option>
            </select></div>
          <div className="col-md-4"><label className="form-label">Date de naissance *</label>
            <input name="ddn" type="date" className="form-control" required value={ddn} onChange={(e) => setDdn(e.target.value)} /></div>
          <div className="col-md-8">
            <label className="form-label">Photo (facultative, JPEG/PNG, 3 Mo max)</label>
            <input type="file" accept="image/jpeg,image/png" className="form-control" onChange={lirePhoto} />
            {photo && <img src={photo} alt="Aperçu" className="mt-2 rounded" style={{ maxWidth: 96, maxHeight: 96, objectFit: 'cover' }} />}
          </div>
        </div>
      </div></div>

      <div className="card adlt-etape mb-3"><div className="card-body">
        <h5 className="d-flex align-items-center gap-2 mb-3"><span className="adlt-etape-num">2</span>Coordonnées</h5>
        <div className="row g-2">
          <div className="col-12"><label className="form-label">Adresse *</label><input name="adresse" className="form-control" required autoComplete="street-address" /></div>
          <div className="col-4 col-md-3"><label className="form-label">NPA *</label><input name="npa" className="form-control" required autoComplete="postal-code" /></div>
          <div className="col-8 col-md-9"><label className="form-label">Localité *</label><input name="localite" className="form-control" required autoComplete="address-level2" /></div>
          <div className="col-md-6"><label className="form-label">Courriel *</label><input name="email" type="email" className="form-control" required autoComplete="email" /></div>
          <div className="col-md-6"><label className="form-label">Téléphone *</label><input name="telephone" type="tel" className="form-control" required autoComplete="tel" /></div>
        </div>
      </div></div>

      {mineur && (
        <div className="card adlt-etape mb-3"><div className="card-body">
          <h5 className="d-flex align-items-center gap-2 mb-3"><span className="adlt-etape-num">3</span>Personnes de contact (parents — facultatif)</h5>
          {[1, 2].map((n) => (
            <div className="row g-2 mb-2" key={n}>
              <div className="col-md-4"><label className="form-label">Parent {n} — nom</label><input name={'parent' + n + '_nom'} className="form-control" /></div>
              <div className="col-md-4"><label className="form-label">Téléphone</label><input name={'parent' + n + '_telephone'} type="tel" className="form-control" /></div>
              <div className="col-md-4"><label className="form-label">Courriel</label><input name={'parent' + n + '_email'} type="email" className="form-control" /></div>
            </div>
          ))}
        </div></div>
      )}

      <div className="card adlt-etape mb-3"><div className="card-body">
        <h5 className="d-flex align-items-center gap-2 mb-3"><span className="adlt-etape-num">{mineur ? 4 : 3}</span>Groupe d'entraînement et message</h5>
        <div className="row g-2">
          <div className="col-md-6"><label className="form-label">Groupe souhaité (facultatif)</label>
            <select name="groupe_id" className="form-select"><option value="">—</option>
              {groupes.map((g) => <option key={g.id} value={g.id}>{g.nom}</option>)}
            </select>
            <small className="text-muted">En cas d'admission, vous serez ajouté(e) au groupe et son moniteur informé.</small></div>
          <div className="col-md-6"><label className="form-label">Message (facultatif)</label><textarea name="message" rows="2" className="form-control"></textarea></div>
        </div>
      </div></div>

      <div className="card adlt-etape mb-3"><div className="card-body">
        <h5 className="d-flex align-items-center gap-2 mb-3"><span className="adlt-etape-num">{mineur ? 5 : 4}</span>Cotisation</h5>
        <div className="row g-3">
          <div className="col-md-6"><label className="form-label d-block">Type de membre *</label>
            <div className="adlt-choix">
              <button type="button" className={`btn ${typeMembre === 'actif' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setTypeMembre('actif')}>Membre actif</button>
              <button type="button" className={`btn ${typeMembre === 'soutien' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setTypeMembre('soutien')}>Membre de soutien</button>
            </div></div>
          <div className="col-md-6"><label className="form-label d-block">Une personne de mon foyer est déjà membre</label>
            <div className="adlt-choix">
              <button type="button" className={`btn ${!foyer ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setFoyer(false)}>Non</button>
              <button type="button" className={`btn ${foyer ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setFoyer(true)}>Oui</button>
            </div></div>
        </div>
        {cotisation ? (
          <div className="alert alert-primary py-2 mt-3 mb-0">
            Catégorie <strong>{cotisation.categorie}</strong> — cotisation annuelle {fmtCHF(cotisation.annuel)}.<br />
            Prorata {cotisation.mois}/12 mois restants{foyer ? ' avec rabais foyer 20 %' : ''} :
            {' '}<strong>{String(cotisation.montant).replace(/\B(?=(\d{3})+(?!\d))/g, "'")} CHF</strong> pour {new Date().getFullYear()}.
            <br /><small className="text-muted">Cotisation SwissArchery incluse. L'année du club commence en janvier.</small>
          </div>
        ) : (
          <p className="text-muted mt-3 mb-0">Indiquez votre date de naissance pour calculer la cotisation.</p>
        )}
      </div></div>

      <div className="card adlt-etape mb-3"><div className="card-body">
        <h5 className="d-flex align-items-center gap-2 mb-3"><span className="adlt-etape-num">{mineur ? 6 : 5}</span>Consentements</h5>
        <div className="form-check mb-2">
          <input className="form-check-input" type="checkbox" name="consentement" id="cand-consent" required />
          <label className="form-check-label" htmlFor="cand-consent">
            Je consens au traitement de mes données personnelles par la Compagnie des Archers de la Tour aux fins du traitement de ma candidature et de la gestion de mon adhésion. *
          </label>
        </div>
        <div className="form-check">
          <input className="form-check-input" type="checkbox" name="statuts" id="cand-statuts" required />
          <label className="form-check-label" htmlFor="cand-statuts">
            J'ai pris connaissance des statuts du club et je les accepte. *
          </label>
        </div>
      </div></div>

      <button className="btn btn-primary btn-lg w-100 w-md-auto" disabled={etat === 'envoi'}>
        {etat === 'envoi' ? 'Envoi…' : 'Envoyer ma candidature'}
      </button>
    </form>
  );
}

/** Gestion des candidatures (comité). */
export function Admissions({ toast }) {
  const [state, reload] = useApi('candidatures', { deps: [] });
  const [detail, setDetail] = useState(null);
  if (state.loading) return <Spinner />;
  if (!state.ok) return <ErrorBox message={state.error} />;

  async function ouvrir(id) {
    const r = await api('candidatures/' + id);
    if (r.ok) setDetail(r.data);
    else toast(r.error, true);
  }

  async function decider(id, statut) {
    const motif = statut === 'refusee' ? (prompt('Motif du refus :') ?? '') : '';
    const r = await api(`candidatures/${id}/decision`, { method: 'POST', body: { statut, motif } });
    if (r.ok) {
      toast(statut === 'acceptee' ? 'Candidature acceptée : compte créé, courriel de bienvenue envoyé.' : 'Décision enregistrée, candidat notifié.');
      setDetail(null);
      reload();
    } else toast(r.error, true);
  }

  return (
    <div>
      <h2 className="h4 mb-2">Gestion des admissions</h2>
      <Intro>Suivez les candidatures d'adhésion et enregistrez les décisions du comité.</Intro>
      <div className="list-group mb-3">
        {state.data.length === 0 && <div className="text-muted p-2">Aucune candidature.</div>}
        {state.data.map((c) => (
          <button type="button" key={c.id} className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
            onClick={() => ouvrir(c.id)}>
            <span>{c.prenom} {c.nom}<br /><small className="text-muted">reçue le {fmtDate(c.cree_le)}</small></span>
            <Badge statut={c.statut} />
          </button>
        ))}
      </div>
      {detail && (
        <div className="card"><div className="card-body">
          <h5 className="d-flex align-items-center gap-2">
            {detail.photo_url && (
              <ImageProtegee url={detail.photo_url} agrandir alt="Photo de la candidature"
                width="56" height="56" className="rounded object-fit-cover" />
            )}
            {detail.prenom} {detail.nom} <Badge statut={detail.statut} /></h5>
          <p className="mb-1"><strong>Naissance :</strong> {detail.date_naissance || '—'} · <strong>Genre :</strong> {detail.genre || '—'}</p>
          <p className="mb-1"><strong>Adresse :</strong> {detail.adresse}, {detail.npa} {detail.localite}</p>
          <p className="mb-1"><strong>Contact :</strong> {detail.email}{detail.telephone ? ' · ' + detail.telephone : ''}</p>
          {(detail.parents || []).map((p, i) => (
            <p className="mb-1" key={i}><strong>Parent {i + 1} :</strong> {p.nom || '—'}{p.telephone ? ' · ' + p.telephone : ''}{p.email ? ' · ' + p.email : ''}</p>
          ))}
          {detail.groupe_id && <p className="mb-1"><strong>Groupe souhaité :</strong> #{detail.groupe_id}</p>}
          {detail.cotisation && <p className="mb-1"><strong>Cotisation :</strong> {fmtCHF(detail.cotisation.montant)} ({detail.cotisation.categorie}{detail.cotisation.rabais_foyer ? ', rabais foyer 20 %' : ''}, prorata)</p>}
          {/* La vignette ci-dessus est cliquable : un lien direct vers la route
              serait rejeté (401) faute d'en-tête d'authentification. */}
          {detail.message && <p className="mb-1"><strong>Message :</strong> {detail.message}</p>}
          <p className="text-muted small">Consentement donné le {detail.consentement_le}{detail.statuts_acceptes_le ? ' · statuts acceptés' : ''} — consultation journalisée (nLPD).</p>
          {['recue', 'en_examen'].includes(detail.statut) && (
            <div className="d-flex gap-2">
              <button className="btn btn-success btn-sm" title="Accepter la candidature et créer le compte du membre"
                onClick={() => decider(detail.id, 'acceptee')}>✓ Admission</button>
              <button className="btn btn-outline-danger btn-sm" onClick={() => decider(detail.id, 'refusee')}>✗ Refuser</button>
              {detail.statut === 'recue' && (
                <button className="btn btn-outline-secondary btn-sm" title="Marquer la candidature en examen"
                  onClick={() => decider(detail.id, 'en_examen')}>En attente</button>
              )}
            </div>
          )}
          {detail.motif_refus && <p className="mt-2 mb-0"><strong>Motif du refus :</strong> {detail.motif_refus}</p>}
        </div></div>
      )}
    </div>
  );
}
